// AGC: Reusable pull-to-refresh hook for mobile scroll-up refresh
// AGC: Provides touch handlers, pull distance state, and refresh indicator props

import { useRef, useState, useCallback } from 'react'

const PULL_THRESHOLD = 60

export function usePullToRefresh(onRefresh: () => Promise<void>) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const touchStartY = useRef(0)
  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // AGC: Only start tracking if scrolled to top
    if (scrollRef.current && scrollRef.current.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY
    } else {
      touchStartY.current = 0
    }
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (touchStartY.current === 0 || isRefreshing) return
    const dy = e.touches[0].clientY - touchStartY.current
    if (dy > 0 && scrollRef.current && scrollRef.current.scrollTop <= 0) {
      // AGC: Dampen pull distance for rubber-band feel (50% of actual drag)
      setPullDistance(Math.min(dy * 0.5, 120))
    }
  }, [isRefreshing])

  const handleTouchEnd = useCallback(async () => {
    if (pullDistance >= PULL_THRESHOLD && !isRefreshing) {
      setIsRefreshing(true)
      // AGC: Haptic feedback on trigger
      try {
        const { Capacitor } = await import('@capacitor/core')
        if (Capacitor.isNativePlatform()) {
          const { Haptics, ImpactStyle } = await import('@capacitor/haptics')
          await Haptics.impact({ style: ImpactStyle.Medium })
        }
      } catch { /* AGC: Haptics not available */ }
      try {
        await onRefresh()
      } finally {
        setIsRefreshing(false)
        setPullDistance(0)
      }
    } else {
      setPullDistance(0)
    }
    touchStartY.current = 0
  }, [pullDistance, isRefreshing, onRefresh])

  return {
    scrollRef,
    pullDistance,
    isRefreshing,
    pullThreshold: PULL_THRESHOLD,
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
  }
}
